import type { GraphemeSvgGlyphStroke } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.grapheme.glyph.svg.entities";
import { hasLength } from "@-/com.npmjs.ts-array-length";
import arrayLength from "@-/com.npmjs.extra-array__length";

/**
 * Every constant and formula in this file was measured against Dong Chinese's stroke player
 * (https://www.dong-chinese.com/dictionary/search/能), which is stock Hanzi Writer:
 * - each stroke is the hanzi-writer-data median polyline, its first point pushed 100 units
 *   (half the brush width) back along the first segment (Hanzi Writer's `extendStart`, so the round
 *   start cap lands outside the outline), stroked 200 units wide and clipped by the stroke outline;
 * - `stroke-dasharray` is that extended polyline's length and `stroke-dashoffset` runs from
 *   `0.999 * length` down to `0` over `(length + 500) / 3` ms with a cosine ease-in-out
 *   (Hanzi Writer writes it as `(rawLength + 600) / 3`; same number);
 * - 250 ms between strokes, 250 ms after the last stroke, then a 400 ms fade and an immediate loop.
 */
export const GRAPHEME_SVG_GLYPH_PAUSE_BETWEEN_STROKES_MS = 250;
export const GRAPHEME_SVG_GLYPH_STROKE_FADE_DURATION_MS = 400;
export const GRAPHEME_SVG_GLYPH_MEDIAN_STROKE_WIDTH = 200;
/** Width and height of one Hanzi Writer glyph cell; strips place cells side by side. */
export const GRAPHEME_SVG_GLYPH_CELL_UNITS = 1024;
/**
 * Hanzi Writer never parks the dash offset at exactly the path length: with `stroke-linecap="round"`
 * a dash boundary landing exactly on the path's end paints a full round cap (a 200-unit dot) at the
 * end of a stroke that hasn't been drawn yet. Scaling the offset by 0.999 keeps that boundary inside
 * the path, so the only cap is at the start where the stroke is about to appear anyway.
 */
export const GRAPHEME_SVG_GLYPH_DASHOFFSET_FACTOR = 0.999;

const STROKE_ANIMATION_SPEED = 1;
const MEDIAN_START_EXTENSION = GRAPHEME_SVG_GLYPH_MEDIAN_STROKE_WIDTH / 2;

function pointAt(median: readonly [number, number][], index: number): readonly [number, number] {
  const point = median[index];
  if (point === undefined) {
    throw new Error(`median point index ${index} is out of bounds (length ${arrayLength(median)})`);
  }
  return point;
}

/**
 * Hanzi Writer's `extendStart`: push the first median point half a brush width back along the
 * first segment. For 能 this turns hanzi-writer-data's `[299, 806]` into Dong Chinese's rendered
 * `M 215.8 861.5`, and the stroke's round start cap then sits outside the clip outline.
 */
function getExtendedMedian(median: readonly [number, number][]): readonly [number, number][] {
  if (hasLength(median, 0)) {
    return [];
  }
  const [firstX, firstY] = pointAt(median, 0);
  if (hasLength(median, 1)) {
    return [[firstX, firstY]];
  }
  const [secondX, secondY] = pointAt(median, 1);
  const firstSegmentLength = Math.hypot(secondX - firstX, secondY - firstY);
  if (firstSegmentLength === 0) {
    return median;
  }
  const extendedFirst: [number, number] = [
    firstX - ((secondX - firstX) / firstSegmentLength) * MEDIAN_START_EXTENSION,
    firstY - ((secondY - firstY) / firstSegmentLength) * MEDIAN_START_EXTENSION,
  ];
  return [extendedFirst, ...median.slice(1)];
}

export function buildGraphemeSvgGlyphMedianPathData(median: readonly [number, number][]): string {
  const extendedMedian = getExtendedMedian(median);
  if (hasLength(extendedMedian, 0)) {
    return "";
  }
  const [firstX, firstY] = pointAt(extendedMedian, 0);
  let data = `M ${firstX} ${firstY}`;
  for (const [x, y] of extendedMedian.slice(1)) {
    data += ` L ${x} ${y}`;
  }
  return data;
}

export function computeGraphemeSvgGlyphMedianLength(median: readonly [number, number][]): number {
  const extendedMedian = getExtendedMedian(median);
  let total = 0;
  for (let i = 1; i < arrayLength(extendedMedian); i += 1) {
    const [previousX, previousY] = pointAt(extendedMedian, i - 1);
    const [x, y] = pointAt(extendedMedian, i);
    total += Math.hypot(x - previousX, y - previousY);
  }
  return total;
}

export function getGraphemeSvgGlyphStrokeAnimationDurationMs(length: number): number {
  return (length + 500) / (3 * STROKE_ANIMATION_SPEED);
}

export function getGraphemeSvgGlyphAnimationDurationMs(
  strokeList: readonly GraphemeSvgGlyphStroke[],
): number {
  if (hasLength(strokeList, 0)) {
    return 0;
  }
  let duration = GRAPHEME_SVG_GLYPH_PAUSE_BETWEEN_STROKES_MS * (arrayLength(strokeList) - 1);
  for (const stroke of strokeList) {
    duration += getGraphemeSvgGlyphStrokeAnimationDurationMs(
      computeGraphemeSvgGlyphMedianLength(stroke.median),
    );
  }
  return duration;
}

export function getGraphemeSvgGlyphAnimationCycleDurationMs(
  strokeList: readonly GraphemeSvgGlyphStroke[],
): number {
  return (
    getGraphemeSvgGlyphAnimationDurationMs(strokeList) +
    GRAPHEME_SVG_GLYPH_PAUSE_BETWEEN_STROKES_MS +
    GRAPHEME_SVG_GLYPH_STROKE_FADE_DURATION_MS
  );
}

export function graphemeSvgGlyphHanziWriterEasing(value: number): number {
  "worklet";
  return (1 - Math.cos(Math.PI * value)) / 2;
}

/**
 * Timeline value at which stroke `strokeIndex` begins (and stroke `strokeIndex - 1` ends). Every
 * timing target and every per-stroke start MUST come from this one expression: `3 * (1 / 10)` is
 * `0.30000000000000004` in IEEE doubles while `3 / 10` is `0.3`, and that 4e-16 gap was enough to
 * flip a not-yet-started stroke to "started" (opacity 1 at full dash offset → a round-cap blob at
 * the stroke's end).
 */
export function getGraphemeSvgGlyphStrokeBoundaryProgress(
  strokeIndex: number,
  totalStrokes: number,
): number {
  "worklet";
  return totalStrokes === 0 ? 0 : strokeIndex / totalStrokes;
}

const LOCAL_PROGRESS_SNAP_EPSILON = 1e-9;

/**
 * Each step keeps its completed preview except while its own stroke is being drawn.
 * Earlier strokes never rewind, and later strokes never appear in this box.
 */
export function getGraphemeSvgGlyphStepProgress(
  progress: number,
  strokeIndex: number,
  totalStrokes: number,
): number {
  "worklet";
  const start = getGraphemeSvgGlyphStrokeBoundaryProgress(strokeIndex, totalStrokes);
  const end = getGraphemeSvgGlyphStrokeBoundaryProgress(strokeIndex + 1, totalStrokes);
  const isDrawing = progress > start && progress < end;
  const isFirstStrokeStarting = strokeIndex === 0 && progress === 0;
  return isDrawing || isFirstStrokeStarting ? progress : end;
}

/**
 * 0..1 progress of a single stroke given the overall 0..1 timeline value. Snapped to exactly 0 / 1
 * within 1e-9 so float drift in the timeline can never leave a stroke a hair "started" or "unfinished".
 */
export function getGraphemeSvgGlyphStrokeLocalProgress(
  progress: number,
  strokeIndex: number,
  totalStrokes: number,
): number {
  "worklet";
  const startProgress = getGraphemeSvgGlyphStrokeBoundaryProgress(strokeIndex, totalStrokes);
  const localProgress = (progress - startProgress) * totalStrokes;
  if (localProgress <= LOCAL_PROGRESS_SNAP_EPSILON) {
    return 0;
  }
  if (localProgress >= 1 - LOCAL_PROGRESS_SNAP_EPSILON) {
    return 1;
  }
  return localProgress;
}

export function getGraphemeSvgGlyphStrokeDashoffset(length: number, localProgress: number): number {
  "worklet";
  return length * GRAPHEME_SVG_GLYPH_DASHOFFSET_FACTOR * (1 - localProgress);
}
